import React from "react";
import { Button, Icon } from "../../";
import {
  getBusinessPOSIntegrations,
  getBusinessSalesChannelIntegrations,
} from "../../../../hooks";

const SalesChannelBusinessCard = ({ business, integration, onSelect }) => {
  const { name, logo, third_party_integrations = [] } = business;

  const businessPOSIntegrations = getBusinessPOSIntegrations(business);
  const businessSalesChannelIntegrations =
    getBusinessSalesChannelIntegrations(business);

  const getIntegrationStatus = (integrationId) => {
    const businessIntegration = third_party_integrations.find(
      (tpi) => tpi.third_party_integration_id === integrationId,
    );

    if (!businessIntegration) return null;

    const isConnected =
      businessIntegration.setup_status === "success" ||
      (businessIntegration.enabled &&
        businessIntegration.setup_status !== null);

    return isConnected ? "connected" : "pending";
  };

  const handleCardClick = () => {
    onSelect?.(business);
  };

  return (
    <Button
      type="button"
      onClick={handleCardClick}
      variant="ghost"
      className="group border-border bg-background hover:border-border hover:bg-card h-full w-full cursor-pointer rounded-lg border p-4 text-left transition-colors"
    >
      {/* Business Info */}
      <div className="flex min-w-0 items-start gap-3">
        {logo ? (
          <img
            src={logo}
            alt={name}
            className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="bg-card flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
            <Icon name="Store" className="text-muted-foreground h-6 w-6" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="font-small min-w-0 flex-1 truncate text-white">
              {name}
            </div>
            <div className="text-muted-foreground flex flex-shrink-0 items-center gap-1 text-xs">
              <span>Connect</span>
              <Icon name="ChevronRight" className="h-4 w-4" />
            </div>
          </div>

          {/* POS Integrations Section */}
          {businessPOSIntegrations.length > 0 && (
            <div className="border-border mt-2 border-t pt-2">
              <div className="text-muted-foreground mb-2 text-xs">
                POS Integrations:
              </div>
              <div className="space-y-1.5">
                {businessPOSIntegrations.map((posIntegration) => {
                  const status = getIntegrationStatus(posIntegration.id);

                  return (
                    <div
                      key={posIntegration.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {posIntegration.logo ? (
                          <img
                            src={posIntegration.logo}
                            alt={posIntegration.name}
                            className="h-5 w-5 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="bg-secondary flex h-5 w-5 flex-shrink-0 items-center justify-center rounded">
                            <Icon
                              name="Package"
                              className="text-muted-foreground h-3 w-3"
                            />
                          </div>
                        )}
                        <span className="text-foreground truncate text-xs">
                          {posIntegration.name}
                        </span>
                      </div>
                      {status && (
                        <span
                          className={`inline-flex flex-shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            status === "connected"
                              ? "border-success/30 bg-success/20 text-success border"
                              : "border-warning/30 bg-warning/20 text-warning border"
                          }`}
                        >
                          {status === "connected" ? "Connected" : "Pending"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sales Channel Integrations Section */}
          {businessSalesChannelIntegrations.length > 0 && (
            <div className="border-border mt-2 border-t pt-2">
              <div className="text-muted-foreground mb-2 text-xs">
                Sales Channels:
              </div>
              <div className="space-y-1.5">
                {businessSalesChannelIntegrations.map((scIntegration) => {
                  const status = getIntegrationStatus(scIntegration.id);
                  const isSelected = scIntegration.id === integration?.id;

                  return (
                    <div
                      key={scIntegration.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {scIntegration.logo ? (
                          <img
                            src={scIntegration.logo}
                            alt={scIntegration.name}
                            className="h-5 w-5 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="bg-secondary flex h-5 w-5 flex-shrink-0 items-center justify-center rounded">
                            <Icon
                              name="ShoppingCart"
                              className="text-muted-foreground h-3 w-3"
                            />
                          </div>
                        )}
                        <span
                          className={`truncate text-xs ${isSelected ? "font-medium text-white" : "text-foreground"}`}
                        >
                          {scIntegration.name}
                        </span>
                      </div>
                      {status && (
                        <span
                          className={`inline-flex flex-shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            status === "connected"
                              ? "border-success/30 bg-success/20 text-success border"
                              : "border-warning/30 bg-warning/20 text-warning border"
                          }`}
                        >
                          {status === "connected" ? "Connected" : "Pending"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Button>
  );
};

export default SalesChannelBusinessCard;
